import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { INDUSTRIES, INDUSTRY_LIST } from "@/data/industries";
import { getOffering } from "@/data/catalog";
import { MOCKUP_CAPTIONS } from "@/data/productExtras";
import DemoFormDialog from "@/components/DemoFormDialog";
import FaqAccordion from "@/components/FaqAccordion";
import FinalCta from "@/components/FinalCta";
import TestimonialGrid from "@/components/TestimonialGrid";
import Breadcrumbs from "@/components/Breadcrumbs";
import WorkflowDiagram from "@/components/WorkflowDiagram";
import InlineCta from "@/components/InlineCta";
import { MOCKUPS } from "@/components/ProductMockups";
import { SectionHeading, WhyWhatsApp } from "@/components/Sections";
import { useSeo, FaqSchema } from "@/lib/seo";
import { BRAND } from "@/data/site";

export default function IndustryPage() {
  const { slug } = useParams();
  const ind = INDUSTRIES[slug];

  useSeo({
    title: ind ? ind.seoTitle : "Page not found | GrabNChat",
    description: ind ? ind.seoDescription : "This page doesn't exist.",
    noindex: !ind,
  });

  if (!ind) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Industry not found</h1>
        <Link to="/" className="inline-block mt-6 text-[#25D366] font-semibold">Back to home →</Link>
      </div>
    );
  }

  const others = INDUSTRY_LIST.filter((x) => x.slug !== slug);
  const features = (ind.features || []).map(getOffering).filter(Boolean);
  const mockups = (ind.mockups || []).filter((k) => MOCKUPS[k]);
  const n = ind.name;

  return (
    <div data-testid={`industry-page-${slug}`}>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="absolute inset-0 bg-dots opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-10">
          <Breadcrumbs items={[{ name: ind.title, path: `/industries/${slug}` }]} />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-16 text-center">
          <div className="text-xs uppercase tracking-widest text-[#25D366] font-semibold mb-4">{ind.title}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">WhatsApp Automation for {n}</h1>
          <p className="mt-5 font-display text-xl sm:text-2xl font-semibold text-slate-800">{ind.hero}</p>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">{ind.sub}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <DemoFormDialog source={slug} defaultMessage={`I run a business in ${n}.`} trigger={
              <Button className="bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full px-7 h-12 font-semibold" data-testid="industry-hero-demo-btn">
                Book Free Demo <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            } />
            <a href={`${BRAND.appUrl}/login`}>
              <Button variant="outline" className="w-full bg-white border-slate-200 hover:border-slate-300 text-slate-900 rounded-full px-7 h-12 font-semibold" data-testid="industry-hero-trial-btn">Start Free Trial</Button>
            </a>
          </div>
          <a href="#workflow" className="mt-5 inline-block text-sm font-semibold text-slate-700 hover:text-[#25D366]">See the {n.toLowerCase()} workflow ↓</a>
        </div>
      </section>


      {/* Industry problem */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="The challenge" title={`Common communication challenges in ${n.toLowerCase()}`} />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {ind.problems.map((pr) => (
              <div key={pr.title} className="rounded-2xl border border-slate-200 bg-white p-6">
                <AlertCircle className="w-6 h-6 text-amber-500" aria-hidden />
                <h3 className="mt-3 font-display text-lg font-semibold text-slate-900">{pr.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{pr.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GrabNChat solution */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="The GrabNChat solution" title={`How GrabNChat solves it for ${n.toLowerCase()} businesses`} />
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {ind.solutions.map((f, i) => (
              <div key={f.title} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-shadow" data-testid={`industry-feature-${i}`}>
                <CheckCircle2 className="w-6 h-6 text-[#25D366]" aria-hidden />
                <h3 className="mt-3 font-display text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry workflow */}
      <section id="workflow" className="py-20 md:py-24 bg-[#0F172A] relative overflow-hidden scroll-mt-20">
        <div className="absolute inset-0 bg-dots opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading dark center eyebrow="Workflow" title={`A typical ${n.toLowerCase()} WhatsApp workflow`} sub="How a customer moves from first message to result with GrabNChat." />
          <div className="mt-12"><WorkflowDiagram dark steps={ind.workflow} /></div>
        </div>
      </section>

      {/* Relevant features */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Features" title={`GrabNChat features for ${n.toLowerCase()}`} />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((o) => (
              <Link key={o.slug} to={`/products/${o.slug}`} className="group min-w-0 bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#25D366] hover:shadow-md transition-all">
                <span className="w-10 h-10 rounded-xl grid place-items-center bg-[#25D366]/10 text-[#25D366]"><o.icon className="w-5 h-5" /></span>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{o.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{o.short}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#25D366]">Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Use cases" title={`What ${n.toLowerCase()} businesses use WhatsApp for`} />
          <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ind.useCases.map((u, i) => (
              <li key={u.title} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`industry-usecase-${i}`}>
                <h3 className="font-display font-semibold text-slate-900">{u.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{u.desc}</p>
              </li>
            ))}
          </ul>
          <InlineCta source={slug} text={`See GrabNChat set up for your ${n.toLowerCase()} business`} secondary={{ to: "/pricing", label: "See pricing" }} />
        </div>
      </section>

      {/* Product screens */}
      {mockups.length > 0 && (
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading eyebrow="Product tour" title={`GrabNChat screens ${n.toLowerCase()} teams use every day`} />
            <div className="mt-12 grid lg:grid-cols-2 gap-10">
              {mockups.map((k) => {
                const M = MOCKUPS[k];
                const [t, d] = MOCKUP_CAPTIONS[k] || [];
                return (
                  <div key={k} className="min-w-0">
                    <M />
                    <h3 className="mt-6 font-display text-lg font-semibold text-slate-900">{t}</h3>
                    <p className="mt-1 text-sm text-slate-600">{d}</p>
                  </div>
                );
              })}
            </div>
            <p className="mt-6 text-xs text-slate-400">Previews use sample data.</p>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Benefits" title={`What changes for your ${n.toLowerCase()} business`} />
          </div>
          <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {ind.benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 rounded-xl bg-white border border-slate-200 p-4 text-slate-800">
                <CheckCircle2 className="w-5 h-5 text-[#25D366] mt-0.5 shrink-0" /> <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">What our customers say</h2>
          <div className="mt-10"><TestimonialGrid /></div>
        </div>
      </section>

      <WhyWhatsApp />

      {/* FAQs */}
      <section className="py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 text-center">WhatsApp for {n}: FAQs</h2>
          <div className="mt-10"><FaqAccordion faqs={ind.faqs} /></div>
          <FaqSchema faqs={ind.faqs} />
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-slate-900">WhatsApp solutions for other industries</h2>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {others.map((o) => (
              <Link key={o.slug} to={`/industries/${o.slug}`} className="bg-white border border-slate-200 rounded-xl p-4 text-sm font-medium text-slate-800 hover:border-[#25D366]" data-testid={`industry-related-${o.slug}`}>
                {o.title}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCta title={`Start Using WhatsApp for Your ${n} Business`} sub="Start a 7-day free trial, or book a free demo and we'll map these workflows to your business." ctaSource={slug} />
    </div>
  );
}
